import {ErrorAction, ReduxAction} from "../../models/common/ActionTypes";
import {getInitialViewSettings, UserViewSettings, UserViewSettingsProp} from "../../models/common/ViewSettings";
import {ApiReturnType} from "../../models/common/ApiReturnType";
import {Team, TeamId, TeamMember} from "../../models/teams/Team";
import {ofType, StateObservable} from "redux-observable";
import {AppState} from "../../reducers/RootReducer";
import {Observable, of} from "rxjs";
import {catchError, map, mergeMap, switchMap} from "rxjs/operators";
import {APP_CONFIG} from "../../config/GeneralConfig";
import {Nullable} from "../../models/common/Nullable";
import {ajax} from "rxjs/ajax";
import {eventNotificationError, eventNotificationSuccess} from "../common/EventNotificationActions";
import {TeamModule} from "../../models/teams/TeamModule";
import {getUrlParamsFromViewSettings, RouteConfig, RouteMethods} from "../../config/RouteConfig";
import {Identifier} from "../../models/common/Identifier";
import {sendUserToPage} from "../common/UserViewSettingsActions";
import {ProjectTeamMemberCard} from "../../components/projects/detail/ProjectTeamMemberCard";


/**
 * Constructs the API path for accessing project teams.
 *
 * @param {Nullable<number>} [id=null] - The optional ID of a specific team. If null, returns the base path for all teams.
 * @return {string} The constructed API path based on the given ID or the base path if no ID is provided.
 */
function apiPath( id: Nullable< number > = null ): string {
    if( id === null ) {
        return APP_CONFIG.baseWeb + 'projects/teams';
    } else {
        return `${ APP_CONFIG.baseWeb }projects/teams/${ id }`;
    }
}

/**
 * Represents all possible action types related to team operations.
 *
 * This type is a union of several specific action type groups that are used
 * in various team-related processes such as fetching teams, fetching a team
 * by its ID, creating a team, or updating a project.
 *
 * Included action type groups:
 * - FetchTeamsActionTypes: Actions related to fetching multiple teams.
 * - FetchTeamByIdActionTypes: Actions related to fetching a single team by its unique identifier.
 * - CreateTeamActionTypes: Actions related to creating a new team.
 * - UpdateProjectActionTypes: Actions related to updating a project associated with a team.
 */
export type TeamActionTypes = FetchTeamsActionTypes
    | FetchTeamByIdActionTypes
    | CreateTeamActionTypes
    | UpdateTeamActionTypes

/**
 * Interface representing an action to fetch teams in a Redux application.
 *
 * This action is used to initiate the process of retrieving a list of teams,
 * typically from an external API or backend service. It extends the base
 * ReduxAction and includes user-specific view settings for customization.
 *
 * The action is identified by the type `FETCH_TEAMS` and may include additional
 * properties from the UserViewSettingsProp interface to tailor the data fetching
 * process based on user preferences or settings.
 *
 * This interface is intended to standardize the structure of the fetch teams
 * action throughout the application, ensuring consistency and type safety.
 */
export interface FetchTeamsAction extends ReduxAction<typeof FETCH_TEAMS>, UserViewSettingsProp {}

/**
 * Represents an action dispatched when fetching teams is successful.
 * This action carries the fetched teams data.
 *
 * It extends the base `ReduxAction` interface with a specific type identifier
 * and includes additional data relevant to the fetched teams.
 *
 * The `teams` property contains the list of teams with their associated members.
 */
export interface FetchTeamsSuccessAction extends ReduxAction<typeof FETCH_TEAMS_SUCCESS> {
    teams: ApiReturnType< Team< TeamMember >[] >
}

/**
 * Represents an action dispatched when there is an error during the process of fetching teams.
 *
 * This action extends the base `ErrorAction` type, where the `type` property is set to `FETCH_TEAMS_ERROR`.
 * It primarily serves to encapsulate error details relevant to the failure of the teams fetch operation.
 *
 * The error data can be used for debugging purposes or to update the application state with error-related
 * information, such as displaying an error message to the user.
 *
 * Extends:
 * - `ErrorAction`
 *
 * Defined Action Type:
 * - `FETCH_TEAMS_ERROR`
 */
export interface FetchTeamsErrorAction extends ErrorAction<typeof FETCH_TEAMS_ERROR> {}

/**
 * Union of all team related action types.
 */
export type FetchTeamsActionTypes = FetchTeamsAction | FetchTeamsSuccessAction | FetchTeamsErrorAction

/**
 * Constant representing the action type for fetching teams.
 * Typically used in state management to dispatch actions related to retrieving
 * team data from a server or other data source.
 */
export const FETCH_TEAMS = "FETCH_TEAMS";
export const FETCH_TEAMS_SUCCESS = "FETCH_TEAMS_SUCCESS";
export const FETCH_TEAMS_ERROR = "FETCH_TEAMS_ERROR";

/**
 * Fetches the teams based on the provided user view settings.
 *
 * @param {UserViewSettings} userViewSettings - The settings defining the user's view preferences.
 * @return {FetchTeamsAction} An action object containing the type and user view settings.
 */
export function fetchTeams( userViewSettings: UserViewSettings ): FetchTeamsAction {
    return { type: FETCH_TEAMS, userViewSettings };
}

/**
 * Handles the successful retrieval of teams from an API call.
 *
 * @param {ApiReturnType<Team<TeamMember>[]>} teams - The list of teams retrieved from the API, including their members.
 * @return {FetchTeamsSuccessAction} An action object containing the type `FETCH_TEAMS_SUCCESS` and the teams data.
 */
export function fetchTeamsSuccess( teams: ApiReturnType<Team<TeamMember>[]> ): FetchTeamsSuccessAction {
    return { type: FETCH_TEAMS_SUCCESS, teams };
}

/**
 * Creates an action to handle errors that occur during the team fetching process.
 *
 * @param {any} error - The error returned or encountered during the fetch teams operation.
 * @return {FetchTeamsErrorAction} An action object containing the type of action and the error information.
 */
export function fetchTeamsError( error: any ) : FetchTeamsErrorAction {
    return { type: FETCH_TEAMS_ERROR, error };
}

/**
 * Handles the `fetchTeams` epic by listening to specific actions, making an API request,
 * and dispatching success or error actions based on the outcome.
 *
 * @param {Observable<FetchTeamsAction>} action$ - The stream of actions dispatched to the store.
 * @param {StateObservable<AppState>} store$ - The state observable representing the current state of the application.
 * @return {Observable} An observable that emits either a success or error action based on the API response.
 */
export function fetchTeamsEpic(
    action$: Observable<FetchTeamsAction>,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType(FETCH_TEAMS),
        switchMap(action => {
            const requestPath = apiPath() + getUrlParamsFromViewSettings(action.userViewSettings);

            return ajax(APP_CONFIG.getRequestObject(requestPath)).pipe(
                map(response => fetchTeamsSuccess(
                    response.response as ApiReturnType< Team<TeamMember>[] >
                )),
                catchError(error => of(
                    fetchTeamsError( error),
                    eventNotificationError(error, store$, TeamModule.identifier, "fetchTeams")
                ))
            );
        })
    )
}

/**
 * Represents an action to fetch a team by its unique identifier.
 * This action extends the functionality of `TeamId` and `ReduxAction`
 * to ensure that it includes the necessary team identification and
 * action type information for Redux state management.
 *
 * The action is dispatched to trigger the asynchronous operation of
 * fetching a team by its ID, typically from a backend API or service.
 *
 * Developers should ensure that the `FETCH_TEAM_BY_ID` action type
 * is properly handled by relevant reducers and middleware to process
 * the request and update the state accordingly.
 */
export interface FetchTeamByIdAction extends TeamId, ReduxAction<typeof FETCH_TEAM_BY_ID> {}

/**
 * Represents the successful outcome of fetching a team's details by its ID.
 * This interface extends both `TeamId` and `ReduxAction`, encapsulating the information
 * about the specific team and the action's type.
 *
 * The `payload` property contains the team data, including its members,
 * upon successfully fetching the team information.
 */
export interface FetchTeamByIdSuccessAction extends TeamId, ReduxAction<typeof FETCH_TEAM_BY_ID_SUCCESS> {
    payload: Team<TeamMember>
}

/**
 * Represents an action dispatched when there is an error during the fetch team by ID operation.
 *
 * This action combines team identification information and error details to indicate
 * that a fetch request for a specific team has failed.
 *
 * The `FETCH_TEAM_BY_ID_ERROR` type is used to specify the nature of the action.
 * The action also includes additional properties associated with the error.
 *
 * This interface extends:
 * - `TeamId`: Represents the unique identifier for the team involved in the operation.
 * - `ErrorAction`: Defines the structure for actions that involve error handling,
 *   including a specific type and an error payload.
 */
export interface FetchTeamByIdErrorAction extends TeamId, ErrorAction<typeof FETCH_TEAM_BY_ID_ERROR> {}

/**
 * Represents the action types associated with fetching a team by its ID.
 * This type is a union of different actions that correspond to the various stages
 * of the fetch operation, such as initiating the fetch, a successful response,
 * or an error occurring during the process.
 *
 * FetchTeamByIdAction - Represents the initiation of the fetch process.
 * FetchTeamByIdSuccessAction - Represents the successful retrieval of the team's data.
 * FetchTeamByIdErrorAction - Represents an error encountered during the fetch operation.
 */
export type FetchTeamByIdActionTypes = FetchTeamByIdAction | FetchTeamByIdSuccessAction | FetchTeamByIdErrorAction

/**
 * A constant representing the action type for fetching a team by its unique identifier.
 *
 * This value is typically used in scenarios where an action is dispatched
 * to retrieve team data from a store, database, or API based on a given team ID.
 *
 * @constant
 * @type {string}
 */
export const FETCH_TEAM_BY_ID = "FETCH_TEAM_BY_ID";
export const FETCH_TEAM_BY_ID_SUCCESS = "FETCH_TEAM_BY_ID_SUCCESS";
export const FETCH_TEAM_BY_ID_ERROR = "FETCH_TEAM_BY_ID_ERROR";

/**
 * Fetches the details of a team by its unique identifier.
 *
 * @param {Identifier<number>} teamId - The unique identifier of the team to fetch.
 * @return {FetchTeamByIdAction} An action object containing the team ID and action type.
 */
export function fetchTeamById( teamId: Identifier<number> ): FetchTeamByIdAction {
    return { type: FETCH_TEAM_BY_ID, id: teamId };
}

/**
 * Creates an action for successfully fetching a team by its ID.
 *
 * @param {Identifier<number>} teamId - The unique identifier of the team.
 * @param {Team<TeamMember>} payload - The team data including its members.
 * @return {FetchTeamByIdSuccessAction} The action object with the type, team ID, and payload.
 */
export function fetchTeamByIdSuccess( teamId: Identifier<number>, payload: Team<TeamMember> ): FetchTeamByIdSuccessAction {
    return { type: FETCH_TEAM_BY_ID_SUCCESS, id: teamId, payload };
}

/**
 * Creates an action indicating that fetching a team by its ID has failed.
 *
 * @param {Identifier<number>} teamId - The unique identifier of the team.
 * @param {any} error - The error object or message describing the failure.
 * @return {FetchTeamByIdErrorAction} An action containing the team ID, the error details, and the action type.
 */
export function fetchTeamByIdError( teamId: Identifier<number>, error: any ): FetchTeamByIdErrorAction {
    return { type: FETCH_TEAM_BY_ID_ERROR, id: teamId, error };
}

/**
 * Handles fetching a team by its ID through an Epic.
 * Listens for the `FETCH_TEAM_BY_ID` action type and performs an API request to fetch the team details.
 *
 * @param {Observable<FetchTeamByIdAction>} action$ - The stream of Redux actions to process.
 * @param {StateObservable<AppState>} store$ - The Redux application state observable.
 * @return {Observable} An observable stream of actions resulting from the API request, either success or error related.
 */
export function fetchTeamByIdEpic(
    action$: Observable< FetchTeamByIdAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( FETCH_TEAM_BY_ID ),
        mergeMap( action => {
            const path = apiPath( action.id.rawValue );

            return ajax( {
                ...APP_CONFIG.getRequestObject( path ),
                timeout: 8000
            } ).pipe(
                map( response => fetchTeamByIdSuccess(
                    action.id,
                    response.response as Team<TeamMember>
                ) ),
                catchError( error => of(
                    fetchTeamByIdError( action.id, error ),
                    eventNotificationError( error, store$, TeamModule.identifier, "fetchTeamById" )
                ) )
            )
        } )
    );
}

/**
 * Represents an action to create a new team in the application.
 *
 * This action is part of the Redux workflow and conforms to the structure of a ReduxAction.
 * It contains the necessary data to define the team being created, including its members.
 *
 * The `type` property is fixed to the `CREATE_TEAM` constant to indicate the specific action type.
 * The `team` property carries the details of the team being created.
 * The `sendUserToPage` property specifies the URL path to redirect the user to after team creation.
 *
 * @template TeamMember - A type representing the structure of a team member.
 */
export interface CreateTeamAction extends ReduxAction<typeof CREATE_TEAM> {
    team: Team<TeamMember>
    sendUserToPage: Nullable<string>
}

/**
 * Represents a Redux action dispatched when a team is successfully created.
 * This action typically carries the information about the created team,
 * including its members, to update the application state.
 *
 * @template T - A generic type representing the team member structure.
 *
 * @extends ReduxAction<typeof CREATE_TEAM_SUCCESS>
 *
 * @property team - The team object containing details of the created team and
 *                  its members. The team object is strongly typed to ensure
 *                  the integrity of the data structure within the application.
 */
export interface CreateTeamSuccessAction extends ReduxAction<typeof CREATE_TEAM_SUCCESS> {
    team: Team<TeamMember>
}

/**
 * Represents an action dispatched when there is an error during the creation of a team.
 * This action extends the base `ErrorAction` and includes additional details about the team being created.
 *
 * @template T - Specifies the type of the team members.
 *
 * @property {Team<TeamMember>} team - The team data associated with the creation process.
 * The `Team` object encapsulates details about the team and its members.
 */
export interface CreateTeamErrorAction extends ErrorAction<typeof CREATE_TEAM_ERROR> {
    team: Team<TeamMember>
}

/**
 * Represents the possible action types related to the creation of a team.
 *
 * This type is a union of specific actions that can be dispatched
 * as part of the team creation process.
 *
 * - `CreateTeamAction`: Action to initiate the creation of a team.
 * - `CreateTeamSuccessAction`: Action dispatched when the team creation is successful.
 * - `CreateTeamErrorAction`: Action dispatched when there is an error during team creation.
 */
export type CreateTeamActionTypes = CreateTeamAction | CreateTeamSuccessAction | CreateTeamErrorAction

/**
 * A constant representing the action type for creating a new team.
 * This value is typically used in applications to signify and handle
 * the operation of team creation, such as in Redux action types
 * or similar state management frameworks.
 */
export const CREATE_TEAM = "CREATE_TEAM";
/**
 * A constant representing the success action type for creating a team.
 *
 * This variable is typically used in actions or reducers within a state
 * management library (e.g., Redux) to signify the successful completion
 * of a team creation process. It acts as an identifier for the corresponding
 * action in the application's state or business logic handling.
 */
export const CREATE_TEAM_SUCCESS = "CREATE_TEAM_SUCCESS";
/**
 * A constant representing an error code or message for failures
 * encountered during the creation of a team.
 *
 * This variable is typically used to identify or handle errors
 * related to team creation processes in the application.
 */
export const CREATE_TEAM_ERROR = "CREATE_TEAM_ERROR";

/**
 * Creates an action to add a new team.
 *
 * IMPORTANT:
 *      When sendUserToPage is set to a non-null value, the user will be redirected to the specified URL path after team creation.
 *      When sendUserToPage is set to null, the user will not be redirected to any page after team creation.
 *
 * @param {Team<TeamMember>} team - The team object containing team details and members.
 * @param {Nullable<string>} sendUserToPage - The URL path to redirect the user to after team creation.
 * @return {CreateTeamAction} The action object with the type `CREATE_TEAM` and the specified team.
 */
export function createTeam(
    team: Team<TeamMember>,
    sendUserToPage: Nullable<string> = null
): CreateTeamAction {
    return {  type: CREATE_TEAM, team, sendUserToPage };
}

/**
 * Creates an action indicating the successful creation of a team.
 *
 * @param {Team<TeamMember>} team - The team object that has been successfully created.
 * @return {CreateTeamSuccessAction} An action object containing the type and the created team.
 */
export function createTeamSuccess(
    team: Team<TeamMember>,
): CreateTeamSuccessAction {
    return { type: CREATE_TEAM_SUCCESS, team };
}

/**
 * Creates an action indicating that an error occurred during team creation.
 *
 * @param {Team<TeamMember>} team - The team data associated with the error.
 * @param {any} error - The error object or message describing the issue.
 * @return {CreateTeamErrorAction} An action object representing the team creation error.
 */
export function createTeamError( team: Team<TeamMember>, error: any ): CreateTeamErrorAction {
    return { type: CREATE_TEAM_ERROR, team, error };
}

/**
 * Handles the creation of a team based on dispatched actions and manages associated side effects.
 * This method is an Epic that processes `CREATE_TEAM` actions, performs an AJAX request to a server
 * to create a new team, and dispatches success or error actions based on the result.
 *
 * @param {Observable<CreateTeamAction>} action$ Stream of dispatched actions containing the team creation details.
 * @param {StateObservable<AppState>} store$ Current state of the application, used for error handling and notifications.
 * @return {Observable} An observable stream of actions resulting from the team creation process, including
 *                      success and error handling actions.
 */
export function createTeamEpic(
    action$: Observable< CreateTeamAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( CREATE_TEAM ),
        switchMap( action => {
            const onSuccess = action.sendUserToPage == null
                ? of(
                    createTeamSuccess( action.team ),
                    fetchTeams( getInitialViewSettings( TeamModule.identifier ) ),
                    eventNotificationSuccess()
                )
                : of(
                    createTeamSuccess( action.team ),
                    fetchTeams( getInitialViewSettings( TeamModule.identifier ) ),
                    eventNotificationSuccess(),
                    sendUserToPage( action.sendUserToPage, TeamModule.identifier )
                );

            return ajax( APP_CONFIG.getRequestObject(
                apiPath(),
                RouteMethods.post,
                action.team
            ) ).pipe(
                mergeMap( _ => onSuccess ),
                catchError( error => {

                    return of(
                        createTeamError( action.team, error ),
                        eventNotificationError( error, store$, 'teams', 'createTeam' )
                    )
                } )
            );
        } )
    )
}

/**
 * Represents an action to update a team's details in the application's state.
 *
 * This action is used to modify an existing team's properties or state.
 * It extends the base ReduxAction with a specific type identifier and
 * additional properties related to team updates.
 *
 * @interface UpdateTeamAction
 * @extends ReduxAction<typeof UPDATE_TEAM>
 *
 * @property {Team<TeamMember>} team - The team object containing updated details.
 * @property {Nullable<string>} sendUserToPage - A URL or route path to redirect the user to a specific page.
 */
export interface UpdateTeamAction extends ReduxAction<typeof UPDATE_TEAM> {
    team: Team<TeamMember>
    sendUserToPage: Nullable<string>
}

/**
 * Represents an action dispatched when a team update operation is successfully completed.
 * This action contains the updated team information as part of its payload.
 *
 * @extends ReduxAction<typeof UPDATE_TEAM_SUCCESS>
 *
 * @template TeamMember - The type representing a member of the team.
 */
export interface UpdateTeamSuccessAction extends ReduxAction<typeof UPDATE_TEAM_SUCCESS> {
    team: Team<TeamMember>
}

/**
 * Represents an action triggered when there is an error in updating a team.
 *
 * This interface extends the base `ErrorAction` type and provides additional context
 * specific to team updates, including the team details associated with the error.
 *
 * The `type` property is pre-defined as `UPDATE_TEAM_ERROR` to uniquely identify this action.
 *
 * @template T Extends the `TeamMember` type, providing flexibility for different team member structures.
 * @extends ErrorAction<typeof UPDATE_TEAM_ERROR> Extends the base error action with a specific type.
 * @property team The team object related to the error, containing information about the team and its members.
 */
export interface UpdateTeamErrorAction extends ErrorAction<typeof UPDATE_TEAM_ERROR> {
    team: Team<TeamMember>
}

/**
 * Represents the possible action types that can occur when updating a team.
 * Combines multiple action variations related to the team update process, such as:
 * - Initiating an update action.
 * - Successfully completing an update action.
 * - Encountering an error during the update process.
 *
 * This type is a union of the following action types:
 * - `UpdateTeamAction`: Indicates the initiation of the team update process.
 * - `UpdateTeamSuccessAction`: Indicates the successful completion of the team update process.
 * - `UpdateTeamErrorAction`: Indicates the failure or error encountered during the team update process.
 */
export type UpdateTeamActionTypes = UpdateTeamAction | UpdateTeamSuccessAction | UpdateTeamErrorAction

/**
 * Represents the action type for updating a team's information.
 * This constant is typically used in a Redux action to identify
 * the specific operation of modifying team-related data within the state.
 */
export const UPDATE_TEAM = "UPDATE_TEAM";
/**
 * Represents the action type dispatched when a team update operation
 * is successfully completed in the application state management process.
 *
 * This constant is typically used in Redux or similar state management
 * libraries to identify the successful completion of an action that
 * updates the data or details of a team.
 */
export const UPDATE_TEAM_SUCCESS = "UPDATE_TEAM_SUCCESS";
/**
 * A constant representing the error type for a team update process.
 * This variable is used to signify that an error occurred while attempting to update a team's information.
 * It can be used for error handling, logging, or as part of application state management.
 */
export const UPDATE_TEAM_ERROR = "UPDATE_TEAM_ERROR";

/**
 * Updates the team information.
 *
 * IMPORTANT:
 *      When sendUserToPage is set to a non-null value, the user will be redirected to the specified URL path after team creation.
 *      When sendUserToPage is set to null, the user will not be redirected to any page after team creation.
 *
 * @param {Team} team - The team object containing updated team details.
 * @param {Nullable<string>} sendUserToPage - A nullable string representing the URL or page to redirect the user to after the update. Can be null if no redirection is needed.
 * @return {UpdateTeamAction} An action object containing the type, updated team, and optional redirection page.
 */
export function updateTeam( team: Team<TeamMember>, sendUserToPage: Nullable<string> = null ): UpdateTeamAction {
    return {  type: UPDATE_TEAM, team, sendUserToPage };
}

/**
 * Updates the team data with success information.
 *
 * @param {Team<TeamMember>} team - The team object containing updated information about the team's members and details.
 * @return {UpdateTeamSuccessAction} An action object with the type `UPDATE_TEAM_SUCCESS` and the updated team data.
 */
export function updateTeamSuccess( team: Team<TeamMember> ): UpdateTeamSuccessAction {
    return { type: UPDATE_TEAM_SUCCESS, team };
}

/**
 * Creates an action to update a team's error state.
 *
 * @param {Team<TeamMember>} team - The team object associated with the error.
 * @param {*} error - The error information to associate with the team.
 * @return {UpdateTeamErrorAction} The action object containing the type, team, and error.
 */
export function updateTeamError( team: Team<TeamMember>, error: any ): UpdateTeamErrorAction {
    return { type: UPDATE_TEAM_ERROR, team, error };
}

/**
 * Handles the process of updating a team's information and managing side effects such as notifications
 * and conditional navigation.
 *
 * @param {Observable<UpdateTeamAction>} action$ - Stream of actions of type `UpdateTeamAction`, which contains the team data and optional navigation routing.
 * @param {StateObservable<AppState>} store$ - Observable of the current application state.
 * @return {Observable} An observable stream of actions, including success or error actions, as well as side effects like notifications and navigation.
 */
export function updateTeamEpic(
    action$: Observable< UpdateTeamAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( UPDATE_TEAM ),
        switchMap( action => {

            console.log( typeof action.team.id.rawValue )

            const onSuccess = action.sendUserToPage == null
                ? of(
                    updateTeamSuccess( action.team ),
                    fetchTeams( getInitialViewSettings( TeamModule.identifier ) ),
                    eventNotificationSuccess()
                )
                : of(
                    updateTeamSuccess( action.team ),
                    fetchTeams( getInitialViewSettings( TeamModule.identifier ) ),
                    eventNotificationSuccess(),
                    sendUserToPage( action.sendUserToPage, TeamModule.identifier )
                );

            return ajax( APP_CONFIG.getRequestObject(
                apiPath( action.team.id.rawValue ),
                RouteMethods.put,
                action.team
            ) ).pipe(
                mergeMap( _ => onSuccess ),
                catchError( error => of(
                        updateTeamError( action.team, error ),
                        eventNotificationError( error, store$, 'teams', 'updateTeam' )
                    )
                )
            );
        } )
    )
}

